import { dbManager } from './db_manager';
import { localFlashcardDb } from './flashcardLocalDb';
import { statsService } from './statsService';
import { getLocalISOString } from '../utils/timezoneUtils';

// Import Supabase for reliable user detection
import { supabase } from './supabaseClient';

const getCurrentUser = async () => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    return session?.user || null;
  } catch { return null; }
};

export interface Flashcard {
  id: string | number;
  user_id?: string;
  book_id?: string;
  original: string;
  translation: string;
  lang_code: string;
  category?: string; // Used for Deck Name
  status?: string;
  easiness: number;
  interval: number;
  repetitions: number;
  next_review: string;
  next_review_date: string;
  last_review?: string;
  created_at?: string;
  updated_at?: string;
  history?: string;
  difficulty_score?: number;
  deck_id?: string;
  words?: string[];
  new_words_in_this_sentence?: number;
  total_unique_words_so_far?: number;
  reviewDueAtTimestamp?: number;
  translations?: string[];
  primary_translation?: string;
  secondary_translations?: string[];
}

export interface LocalFeedback {
  card_id: string;
  history: string;
  difficulty_score: number;
  next_review_date: string;
  updated_at: string;
  reviewDueAtTimestamp?: number;
}

/**
 * Clean raw AI text to extract only the translated text.
 */
const cleanTranslationText = (text: string): string => {
  if (!text) return '';
  const lines = text.split('\n');
  const translationLine = lines.find(l => /^(tradução|translation):\s*/i.test(l.trim()));

  if (translationLine) {
    return translationLine
      .replace(/^(tradução|translation):\s*/i, '')
      .replace(/[\*#_~`>]/g, '')
      .trim();
  }

  const fallbackLine = lines.find(l => !/^(idioma detectado|detected language|comentário|commentary):\s*/i.test(l.trim()));

  return (fallbackLine || lines[0] || '')
    .replace(/[\*#_~`>]/g, '')
    .trim();
};

/**
 * Extract language name from raw AI response. - REMOVED for strict explicit codes
 */
let isPersistentSyncing = false;

const processPersistentQueue = async () => {
  if (isPersistentSyncing) return;
  
  const queueCount = await localFlashcardDb.getSyncQueueCount();
  if (queueCount === 0) return;

  isPersistentSyncing = true;
  while (true) {
    const task = await localFlashcardDb.getNextSyncTask();
    if (!task) break;

    try {
      if (task.type === 'srs') {
        // 1. Update SRS metadata in D1 flashcards table
        await flashcardManager.updateSrs(task.card_id, task.quality as any);
        // 2. Sync feedback history from IndexedDB to D1 flashcards_feedback table
        await flashcardManager.syncFeedbackToD1(task.card_id);
      }
      
      await localFlashcardDb.dequeueSync(task.id!);
    } catch (e) {
      console.error('Persistent sync task failed:', e);
      // If fatal error, we stop and wait for next trigger to avoid infinite loops
      break; 
    }
    await new Promise(r => setTimeout(r, 1000)); // Rate limit: 1 task per second (Debounce/Throttle)
  }
  
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('libritui-stats-synced'));
  }
  isPersistentSyncing = false;
};

// Attempt to process queue on startup
if (typeof window !== 'undefined') {
  setTimeout(async () => {
    processPersistentQueue();
  }, 3000);
}

export const flashcardManager = {
  processPersistentQueue,
  /**
   * Save a single flashcard to D1.
   */
  saveFlashcard: async (original: string, translationRaw: string, sourceLanguage?: string, bookId?: string, category?: string, deckId?: string): Promise<void> => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      const finalLangCode = sourceLanguage || 'en';

      await dbManager.from('flashcards', user.id).upsert({
        user_id: user.id,
        book_id: bookId ? String(bookId) : '',
        original: original.trim(),
        translation: cleanTranslationText(translationRaw),
        lang_code: finalLangCode || 'en',
        category: category || 'Geral',
        deck_id: deckId || null,
        status: 'active',
        easiness: 2.5,
        interval: 0,
        repetitions: 0,
        next_review: new Date().toISOString(),
        next_review_date: new Date().toISOString().split('T')[0]
      });
    } catch (error) {
      console.error('Error saving flashcard:', error);
      throw error; // Re-throw to allow queue detection
    }
  },

  /**
   * Batch save flashcards (Sync/Import).
   */
  saveFlashcardsBatch: async (cards: { original: string, translation: string, language?: string, book_id?: string }[]): Promise<void> => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      for (const c of cards) {
        // Map language or lang_code to lang_code
        const itemLangCode = (c as any).lang_code || c.language || 'en';

        const payload: any = {
          user_id: user.id,
          book_id: String(c.book_id || ''),
          original: c.original.trim(),
          translation: c.translation.trim(),
          lang_code: itemLangCode,
          deck_id: (c as any).deck_id || null,
          category: (c as any).category || 'Importado',
          status: 'active',
          easiness: 2.5,
          interval: 0,
          repetitions: 0,
          next_review: new Date().toISOString(),
          next_review_date: new Date().toISOString().split('T')[0]
        };

        // Ensure card always has an ID to prevent overlap
        payload.id = (c as any).id ? String((c as any).id) : crypto.randomUUID();

        await dbManager.from('flashcards', user.id).upsert(payload);
      }
    } catch (error) {
      console.error('Error batch saving flashcards:', error);
    }
  },

  /**
   * Fetch all flashcards for current user from D1.
   */
  getFlashcards: async (): Promise<Flashcard[]> => {
    try {
      const user = await getCurrentUser();
      if (!user) return [];

      // 1. Fetch D1 (JOIN with progress table - NO DATE FILTER) (with local cache support)
      let cards: Flashcard[] = [];
      const cacheCardsKey = `libritui_cache_d1_flashcards_${user.id}`;
      const cachedCardsStr = localStorage.getItem(cacheCardsKey);
      if (cachedCardsStr) {
        cards = JSON.parse(cachedCardsStr);
      } else {
        const results = await dbManager.from('flashcards', user.id).select(
          {}, // Empty filters means "all cards for this user"
          undefined, // options
          'flashcards_progress' // Use the progress join
        );
        cards = (results || []) as Flashcard[];
        localStorage.setItem(cacheCardsKey, JSON.stringify(cards));
      }
      
      // 2. Sync missing/newer feedback from D1 (when online) to enable cross-device progress sync
      const localFeedbackMap = await flashcardManager.syncFeedbackFromD1();

      // 4. Merge feedback and optimistic queue counts
      const queueCountMap: Record<string, number> = {};
      // Note: We'd need a method to get all sync tasks to accurately count repetitions
      // For now, we prioritize current display consistency

      cards = cards.map(c => {
        const idStr = String(c.id);
        const feedback = localFeedbackMap[idStr];
        let repetitions = c.repetitions || 0;
        
        return { 
          ...c, 
          repetitions,
          history: feedback?.history || c.history || '',
          difficulty_score: feedback?.difficulty_score ?? c.difficulty_score ?? 0,
          next_review_date: feedback?.next_review_date || c.next_review_date
        };
      })      // Integrate Imported Decks from Store (Read from IndexedDB / JSON)
      try {
        let importedDecks: any[] = [];
        const cacheImportedKey = `libritui_cache_d1_flashcards_imported_${user.id}`;
        const cachedImportedStr = localStorage.getItem(cacheImportedKey);
        if (cachedImportedStr) {
          importedDecks = JSON.parse(cachedImportedStr);
        } else {
          const importedRes = await dbManager.from('flashcards_imported', user.id).select({ user_id: user.id });
          importedDecks = Array.isArray(importedRes) ? importedRes : (importedRes?.data || []);
          localStorage.setItem(cacheImportedKey, JSON.stringify(importedDecks));
        }

        for (const deck of importedDecks) {
          const localKey = deck.caminho_local_do_arquivo || `fc_store_deck_${deck.idioma_nativo}_${deck.idioma_alvo}_${deck.deck_id}`;
          let parsed: any[] | null = await localFlashcardDb.getStoreDeck(localKey);
          
          if (!parsed) {
            const fallbackKey1 = `fc_store_deck_${deck.idioma_nativo}_${deck.idioma_alvo}_${deck.deck_id}`;
            parsed = await localFlashcardDb.getStoreDeck(fallbackKey1);
          }
          if (!parsed) {
            const fallbackKey2 = `fc_store_deck_${deck.deck_id}`;
            parsed = await localFlashcardDb.getStoreDeck(fallbackKey2);
          }

          // Migration from localStorage to IndexedDB
          if (!parsed) {
            const lsValue = localStorage.getItem(localKey);
            if (lsValue) {
              try {
                const lsParsed = JSON.parse(lsValue);
                if (Array.isArray(lsParsed) && lsParsed.length > 0) {
                  await localFlashcardDb.saveStoreDeck(localKey, lsParsed);
                  parsed = lsParsed;
                  localStorage.removeItem(localKey); // Free localStorage space!
                  console.log(`[FlashcardManager] Migrated ${localKey} from localStorage to IndexedDB`);
                }
              } catch (e) {}
            }
          }

          // Fallback migration for older imports that used the shared fc_store_deck_${deck_id} key
          if (!parsed && !deck.caminho_local_do_arquivo) {
            const oldKey = `fc_store_deck_${deck.deck_id}`;
            const oldRawJson = localStorage.getItem(oldKey);
            if (oldRawJson) {
              try {
                const parsedOld = JSON.parse(oldRawJson);
                const hasWordsOld = Array.isArray(parsedOld) && parsedOld.length > 0 &&
                  (parsedOld[0][`${deck.idioma_alvo}-words`] !== undefined || parsedOld[0][`${deck.idioma_alvo}_words`] !== undefined);
                if (hasWordsOld) {
                  await localFlashcardDb.saveStoreDeck(localKey, parsedOld);
                  parsed = parsedOld;
                  localStorage.removeItem(oldKey); // Free localStorage space!
                  console.log(`[FlashcardManager] Successfully migrated old deck ${deck.deck_id} to IndexedDB key: ${localKey}`);
                }
              } catch (e) {}
            }
          }

          if (!parsed) {
            parsed = [];
          }

          if (parsed.length === 0) {
            // Auto-redownload missing or old format deck JSON in background
            try {
              console.log(`[FlashcardManager] Missing or old local JSON format for imported deck ${deck.deck_id}. Downloading Com Palavras version...`);
              let data: any[] = [];
              const levelUpper = deck.deck_id.toUpperCase();
              
              if (deck.deck_id.startsWith('words_')) {
                const level = deck.deck_id.replace('words_', '').toUpperCase();
                const WORD_LANG_FOLDERS: Record<string, string> = {
                  'en': 'Ingles', 'english': 'Ingles',
                  'pt': 'Portugues', 'ptbr': 'Portugues', 'portuguese': 'Portugues',
                  'fr': 'Frances', 'french': 'Frances',
                  'es': 'Espanhol', 'spanish': 'Espanhol',
                  'de': 'Alemao', 'german': 'Alemao',
                  'it': 'Italiano', 'italian': 'Italiano',
                  'ru': 'Russo', 'russian': 'Russo',
                  'hu': 'Hungaro', 'hungarian': 'Hungaro'
                };
                const WORD_SHORT_CODES: Record<string, string> = {
                  'en': 'en', 'english': 'en',
                  'pt': 'pt', 'ptbr': 'pt', 'portuguese': 'pt',
                  'fr': 'fr', 'french': 'fr',
                  'es': 'es', 'spanish': 'es',
                  'de': 'de', 'german': 'de',
                  'it': 'it', 'italian': 'it',
                  'ru': 'ru', 'russian': 'ru',
                  'hu': 'hu', 'hungarian': 'hu'
                };
                const targetFolder = WORD_LANG_FOLDERS[deck.idioma_alvo || 'en'] || 'Ingles';
                const srcCode = WORD_SHORT_CODES[deck.idioma_alvo || 'en'] || 'en';
                const tgtCode = WORD_SHORT_CODES[deck.idioma_nativo || 'pt'] || 'pt';

                const url1 = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/new-flashcards/${targetFolder}/${level}/${srcCode}-${tgtCode}.json?t=${Date.now()}`;
                const url2 = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-backup/new-flashcards/${targetFolder}/${level}/${srcCode}-${tgtCode}.json?t=${Date.now()}`;

                let res = await fetch(url1).catch(() => null);
                if (!res || !res.ok) res = await fetch(url2).catch(() => null);
                if (res && res.ok) {
                  const json = await res.json();
                  const list = Array.isArray(json) ? json : (json[level] || json[level.toLowerCase()] || []);
                  data = list.map((item: any, idx: number) => {
                    const primaryTrans = (Array.isArray(item.translations) && item.translations.length > 0)
                      ? item.translations[0]
                      : (item.translation || item.word || '');

                    const secondaryTrans = (Array.isArray(item.translations) && item.translations.length > 1)
                      ? item.translations.slice(1)
                      : [];

                    return {
                      id_base: `w_${level}_${item.id || idx + 1}`,
                      word: item.word || '',
                      translations: item.translations || [primaryTrans, ...secondaryTrans],
                      primary_translation: primaryTrans,
                      secondary_translations: secondaryTrans,
                      [deck.idioma_alvo || 'en']: item.word || '',
                      [deck.idioma_nativo || 'ptbr']: primaryTrans
                    };
                  });
                }
              } else if (levelUpper === 'A1') {
                const R2_LANG_NAMES: Record<string, string> = {
                  'de': 'Alemão',
                  'es': 'Espanhol',
                  'fr': 'Francês',
                  'hu': 'Húngaro',
                  'en': 'Inglês',
                  'it': 'Italiano',
                  'ptbr': 'Português',
                  'ru': 'Russo'
                };
                
                const targetLangName = R2_LANG_NAMES[deck.idioma_alvo || ''] || deck.idioma_alvo;
                const nativeLangName = R2_LANG_NAMES[deck.idioma_nativo || ''] || deck.idioma_nativo;
                
                const targetUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/A1/${encodeURIComponent(targetLangName)} - A1.json?t=${Date.now()}`;
                const nativeUrl = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/A1/${encodeURIComponent(nativeLangName)} - A1.json?t=${Date.now()}`;

                const [targetRes, nativeRes] = await Promise.all([
                  fetch(targetUrl),
                  fetch(nativeUrl)
                ]);

                if (targetRes.ok && nativeRes.ok) {
                  const targetData = await targetRes.json();
                  const nativeData = await nativeRes.json();

                  if (Array.isArray(targetData) && Array.isArray(nativeData)) {
                    data = targetData.map((item: any, idx: number) => {
                      const nativeItem = nativeData[idx] || {};
                      return {
                        id_base: String(idx + 1),
                        [deck.idioma_alvo || 'en']: item.text || '',
                        [deck.idioma_nativo || 'ptbr']: nativeItem.text || ''
                      };
                    });
                  }
                }
              } else {
                const file = `${deck.deck_id}_Consolidado_Global.json`;
                const url = `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/flashcards-store/R2-consolidated-flashcards/words-counted-new/${file}`;
                const res = await fetch(url);
                if (res.ok) {
                  data = await res.json();
                }
              }

              if (Array.isArray(data) && data.length > 0) {
                await localFlashcardDb.saveStoreDeck(localKey, data);
                parsed = data;
                deck.caminho_local_do_arquivo = localKey;
                
                // Clear imported decks cache key so subsequent calls read updated caminho_local_do_arquivo
                localStorage.removeItem(cacheImportedKey);

                // Also update caminho_local_do_arquivo in D1 Database to keep it synced
                await dbManager.from('flashcards_imported', user.id).update({
                  caminho_local_do_arquivo: localKey
                }, {
                  deck_id: deck.deck_id,
                  idioma_nativo: deck.idioma_nativo,
                  idioma_alvo: deck.idioma_alvo
                }).catch(() => {});
              }
            } catch (dlErr) {
              console.error(`[FlashcardManager] Auto-redownload failed for ${deck.deck_id}:`, dlErr);
            }
          }
          if (Array.isArray(parsed) && parsed.length > 0) {
            try {
              parsed.forEach((item: any) => {
                const cardId = `imported_${deck.deck_id}_${deck.idioma_nativo}_${deck.idioma_alvo}_${item.id_base || item.id || Math.random().toString(36).substring(2, 7)}`;
                const feedback = localFeedbackMap[cardId];

                const primaryTrans = item.primary_translation || (Array.isArray(item.translations) && item.translations.length > 0 ? item.translations[0] : (item[deck.idioma_nativo] || item.translation || item.word || ''));
                const secondaryTrans = item.secondary_translations || (Array.isArray(item.translations) && item.translations.length > 1 ? item.translations.slice(1) : []);

                const fc: Flashcard = {
                  id: cardId,
                  user_id: user.id,
                  original: item[deck.idioma_alvo] || item.word || item.phrase || item.text || '',
                  translation: item[deck.idioma_nativo] || primaryTrans || item.translation || '',
                  translations: item.translations || (primaryTrans ? [primaryTrans, ...secondaryTrans] : []),
                  primary_translation: primaryTrans,
                  secondary_translations: secondaryTrans,
                  lang_code: deck.idioma_alvo,
                  category: `Nível ${deck.deck_id}`,
                  status: 'new',
                  easiness: 2.5,
                  interval: 0,
                  repetitions: 0,
                  next_review: '',
                  next_review_date: feedback?.next_review_date || '',
                  history: feedback?.history || '',
                  difficulty_score: feedback?.difficulty_score ?? 0,
                  deck_id: `${deck.deck_id}_${deck.idioma_nativo}_${deck.idioma_alvo}`,
                  words: item[`${deck.idioma_alvo}-words`] || item[`${deck.idioma_alvo}_words`] || [],
                  new_words_in_this_sentence: item[`${deck.idioma_alvo}-new_words_in_this_sentence`] || item[`${deck.idioma_alvo}_new_words_in_this_sentence`] || 0,
                  total_unique_words_so_far: item[`${deck.idioma_alvo}-total_unique_words_so_far`] || item[`${deck.idioma_alvo}_total_unique_words_so_far`] || 0
                };
                cards.push(fc);
              });
            } catch (jsonErr) {
              console.error(`[FlashcardManager] Error parsing imported deck JSON for ${deck.deck_id}:`, jsonErr);
            }
          }
        }
      } catch (importedErr) {
        console.error('[FlashcardManager] Failed to fetch imported decks from D1:', importedErr);
      }

      // 5. Anti-Acúmulo Cleanup (Async)
      flashcardManager.cleanupLocalCache().catch(() => {});

      console.log(`[FlashcardManager] Fetched ${cards.length} flashcards (including virtual imported) for user ${user.id}`);
      return cards;
    } catch (error) {
      console.error('Error fetching flashcards:', error);
      return [];
    }
  },

  /**
   * Cleanup local cache based on 30-day policy.
   */
  cleanupLocalCache: async (): Promise<void> => {
    try {
      await localFlashcardDb.cleanupOldFeedback(30);
    } catch (err) {
      console.error("[FlashcardManager] Cache cleanup failed:", err);
    }
  },

  /**
   * Sync D1 feedback to local IndexedDB and return updated localFeedbackMap.
   * Standardizes dates to handle cross-browser ISO differences (e.g. Safari/iOS vs Chrome).
   */
  syncFeedbackFromD1: async (): Promise<Record<string, LocalFeedback>> => {
    try {
      const user = await getCurrentUser();
      const localFeedbackMap = await localFlashcardDb.getAllFeedback();
      if (!user) return localFeedbackMap;

      if (typeof navigator !== 'undefined' && navigator.onLine) {
        try {
          const feedbackResults = await dbManager.from('flashcards_feedback', user.id).select().catch(() => []);
          if (Array.isArray(feedbackResults) && feedbackResults.length > 0) {
            const safeParseTime = (dateVal: any): number => {
              if (!dateVal) return 0;
              if (typeof dateVal === 'number') return dateVal;
              const str = String(dateVal).trim();
              const isoStr = str.includes(' ') && !str.includes('T') ? str.replace(' ', 'T') : str;
              const t = new Date(isoStr).getTime();
              return isNaN(t) ? 0 : t;
            };

            for (const f of feedbackResults) {
              const cardId = String(f.card_id);
              const existingLocal = localFeedbackMap[cardId];
              const d1Time = safeParseTime(f.updated_at || f.last_review);
              const localTime = safeParseTime(existingLocal?.updated_at);

              if (!existingLocal || d1Time >= localTime || (f.history && f.history !== existingLocal?.history)) {
                const freshFeedback: LocalFeedback = {
                  card_id: cardId,
                  history: f.history,
                  difficulty_score: f.difficulty_score ?? 0,
                  next_review_date: f.next_review_date,
                  updated_at: f.updated_at || new Date().toISOString()
                };
                await localFlashcardDb.saveFeedback(freshFeedback);
                localFeedbackMap[cardId] = freshFeedback;
              }
            }
          }
        } catch (syncErr) {
          console.warn("[FlashcardManager] Error syncing D1 feedback:", syncErr);
        }
      }
      return localFeedbackMap;
    } catch (err) {
      console.error("[FlashcardManager] syncFeedbackFromD1 error:", err);
      return {};
    }
  },

  /**
   * Update a specific flashcard in D1.
   */
  updateFlashcard: async (id: string | number, updates: Partial<Flashcard>): Promise<void> => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      await dbManager.from('flashcards', user.id).update(updates, { id });
    } catch (error) {
      console.error('Error updating flashcard:', error);
    }
  },

  /**
   * Calculate next SRS state based on quality.
   */
  calculateNextSrsState: (card: Flashcard, quality: 'again' | 'hard' | 'good' | 'easy'): { interval: number, easiness: number, repetitions: number, nextReview: Date, nextReviewDate: string, label: string, reviewDueAtTimestamp: number } => {
    const now = new Date();
    const nextReview = new Date(now);
    let nextInterval = 0;
    let label = '';

    // Regra de Ouro: Intervalos fixos independente do histórico
    switch (quality) {
      case 'again':
        nextInterval = 0;
        nextReview.setMinutes(now.getMinutes() + 1);
        label = '< 1 min';
        break;
      case 'hard':
        nextInterval = 0;
        nextReview.setMinutes(now.getMinutes() + 10);
        label = '< 10 min';
        break;
      case 'good':
        nextInterval = 1;
        nextReview.setDate(now.getDate() + 1);
        nextReview.setHours(4, 0, 0, 0); 
        label = '1 dia';
        break;
      case 'easy':
        nextInterval = 4;
        nextReview.setDate(now.getDate() + 4);
        nextReview.setHours(4, 0, 0, 0); 
        label = '4 dias';
        break;
    }

    const nextReviewDate = getLocalISOString(nextReview).split('T')[0];

    return { 
      interval: nextInterval, 
      easiness: 2.5, // Ease Factor desativado (fixo)
      repetitions: (card.repetitions || 0) + 1, 
      nextReview,
      nextReviewDate,
      label,
      reviewDueAtTimestamp: nextReview.getTime()
    };
  },

  /**
   * Update SRS metadata and study log in D1.
   */
  updateSrs: async (id: string | number, quality: 'again' | 'hard' | 'good' | 'easy'): Promise<void> => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      // 1. Fetch specific card data
      const results = await dbManager.from('flashcards', user.id).select({ id: String(id) });
      const card = results && results.length > 0 ? results[0] : null;

      if (!card) throw new Error('Card not found');

      const now = new Date();
      const { interval: nextInterval, easiness: nextEasiness, repetitions: nextRepetitions, nextReview, nextReviewDate } = 
        flashcardManager.calculateNextSrsState(card, quality);

      // 2. Update Flashcard (Static Metadata Only)
      await dbManager.execute({
        action: 'upsert',
        table: 'flashcards',
        user_id: user.id,
        data: {
          id: card.id,
          user_id: card.user_id || user.id,
          deck_id: card.deck_id || '',
          original: card.original,
          translation: card.translation || '',
          lang_code: card.lang_code || 'en',
          easiness: nextEasiness,
          interval: nextInterval,
          repetitions: nextRepetitions,
          next_review: nextReview.toISOString(),
          last_review: now.toISOString(),
          updated_at: now.toISOString()
        }
      });

    } catch (err) {
      console.error('Error in updateSrs:', err);
    }
  },

  /**
   * Enqueue SRS update for background processing.
   */
  enqueueSrsUpdate: async (card: Flashcard, quality: 'again' | 'hard' | 'good' | 'easy', history?: string, nextReviewDate?: string): Promise<void> => {
    // 1. Instant optimistic UI Update for Graph
    const user = await getCurrentUser();
    if (user?.id) {
      const cleanLang = (card.lang_code || 'en').split('-')[0].toLowerCase();
      const { statsService } = await import('./statsService');
      await statsService.increment('cards_reviewed', user.id, 1, { deck_id: card.deck_id, langCode: cleanLang });
      // Force immediate flush to ensure user_daily_logs is updated instantly without delays
      statsService.flush(user.id);
      
      // 2. Track Feedback History (Immediate Local Write + Enqueue Sync)
      await flashcardManager.trackFeedbackHistory(card, quality, history, nextReviewDate);
    }

    processPersistentQueue();
  },

  /**
   * Track feedback history in flashcards_feedback table.
   * Architecture: Time-compressed string (FIFO 5 items)
   */
  trackFeedbackHistory: async (card: Flashcard, quality: 'again' | 'hard' | 'good' | 'easy', providedHistory?: string, nextReviewDate?: string): Promise<void> => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      // Centralized Mapping Rule: 1=Again, 2=Hard, 3=Good, 4=Easy
      const qualityMap: Record<string, string> = { 
        again: '1', 
        hard: '2', 
        good: '3', 
        easy: '4' 
      };
      const qualityId = qualityMap[quality];
      const today = new Date().toLocaleDateString('sv-SE');
      const newEntry = `${qualityId}:${today}`;

      // --- LOG HISTÓRICO PARA RELATÓRIOS (Assíncrono) ---
      const logReviewEvent = async () => {
        try {
          const now = new Date();
          // Força a montagem da string respeitando o fuso local do dispositivo do usuário
          const localTimestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}`;

          const safeLangCode = card.lang_code || 'fr';

          await dbManager.from('flashcards_review_logs', user.id).insert({
            id: crypto.randomUUID(),
            user_id: user.id || null,
            card_id: String(card.id),
            deck_id: card.deck_id || null,
            rating: Number(qualityId),
            lang_code: safeLangCode,
            created_at: localTimestamp
          });
        } catch (e) {
          console.error('[Flashcards] Falha ao registrar log de revisão:', e);
        }
      };
      logReviewEvent(); // Não usamos await para não bloquear o fluxo
      // ------------------------------------------------

      let updatedHistory = providedHistory;

      if (!updatedHistory) {
        // Fallback: Get existing history from IndexedDB (Offline-First)
        const existing = await localFlashcardDb.getFeedback(String(card.id));
        let history = existing?.history || '';

        // Append and keep last 5 (FIFO)
        let historyArray = history ? history.split(',') : [];
        historyArray.push(newEntry);
        if (historyArray.length > 5) {
          historyArray = historyArray.slice(-5);
        }
        updatedHistory = historyArray.join(',');
      }

      // Re-calculate difficulty based on final history
      const historyArray = updatedHistory.split(',').filter(Boolean);

      // 3. Calculate difficulty score
      const weightMap: Record<string, number> = { '1': 100, '2': 50, '3': 20, '4': 0 };
      const weights = historyArray.map(item => weightMap[item.split(':')[0]] ?? 0);
      const avgWeight = weights.reduce((a, b) => a + b, 0) / (weights.length || 1);
      const difficultyScore = Math.round(avgWeight);

      const now = new Date().toISOString();
      console.log(`[FlashcardSync] Salvando Feedback ID: ${card.id} | History: ${updatedHistory} | Next: ${nextReviewDate}`);

      // 4. Save to IndexedDB Immediately (Zero Latency UI)
      await localFlashcardDb.saveFeedback({
        card_id: String(card.id),
        history: updatedHistory,
        difficulty_score: difficultyScore,
        next_review_date: nextReviewDate || today, // Use provided date or today as fallback
        updated_at: now
      });

      // 4b. [Fix "SEM HISTÓRICO"] Update the localStorage D1 cards cache for non-Store decks.
      // Store/R2 decks (imported_*) already work correctly, so we skip them.
      try {
        const isStoreCard = String(card.deck_id || '').startsWith('imported_');
        if (!isStoreCard && typeof localStorage !== 'undefined') {
          const cacheKey = `libritui_cache_d1_flashcards_${user.id}`;
          const cached = localStorage.getItem(cacheKey);
          if (cached) {
            const cards = JSON.parse(cached);
            const idx = cards.findIndex((c: any) => String(c.id) === String(card.id));
            if (idx !== -1) {
              cards[idx] = { ...cards[idx], history: updatedHistory };
              localStorage.setItem(cacheKey, JSON.stringify(cards));
            }
          }
        }
      } catch (cacheErr) {
        // Non-critical: cache update failure is silent
      }

      // 5. Instant Sync to D1 (flashcards_feedback)
      dbManager.from('flashcards_feedback', user.id).upsert({
        user_id: user.id,
        card_id: String(card.id),
        history: updatedHistory,
        difficulty_score: difficultyScore,
        next_review_date: nextReviewDate || today,
        last_review: now,
        updated_at: now
      }).catch(e => {
        console.error("Instant sync failed, enqueuing...", e);
        // Fallback: Enqueue D1 Sync only if instant sync fails
        localFlashcardDb.enqueueSync({
          type: 'srs',
          card_id: String(card.id),
          quality,
          timestamp: Date.now()
        });
      });

    } catch (err) {
      console.error("[Feedback History] Error tracking:", err);
    }
  },

  /**
   * Sync local IndexedDB feedback to Cloudflare D1.
   * Called by the background queue processor.
   */
  syncFeedbackToD1: async (cardId: string): Promise<void> => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      const local = await localFlashcardDb.getFeedback(cardId);
      if (!local) return;

      await dbManager.from('flashcards_feedback', user.id).upsert({
        user_id: user.id,
        card_id: String(cardId),
        history: local.history,
        difficulty_score: local.difficulty_score,
        next_review_date: local.next_review_date,
        last_review: local.updated_at, // Move last_review tracking here
        updated_at: local.updated_at
      });
    } catch (err) {
      console.error("[FlashcardManager] D1 Feedback Sync failed:", err);
      throw err; // Propagate to queue processor
    }
  },


  /**
   * Delete flashcard from D1.
   */
  deleteFlashcard: async (id: string | number): Promise<void> => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      await dbManager.from('flashcards', user.id).delete(id);
    } catch (error) {
      console.error('Error deleting flashcard:', error);
    }
  },

  /**
   * Delete an entire deck of flashcards from D1.
   */
  deleteFlashcardDeck: async (categoryOrLang: string): Promise<void> => {
    try {
      const user = await getCurrentUser();
      if (!user) return;
      
      const parts = categoryOrLang.split('_');
      if (parts.length >= 3) {
        // Imported deck: [deck_id]_[idioma_nativo]_[idioma_alvo]
        const target = parts[parts.length - 1];
        const native = parts[parts.length - 2];
        const deckId = parts.slice(0, parts.length - 2).join('_');

        await dbManager.from('flashcards_imported', user.id).deleteMatch({
          deck_id: deckId,
          idioma_nativo: native,
          idioma_alvo: target
        }).catch(() => {});
        
        await dbManager.from('flashcards_imported', user.id).deleteMatch({
          id: categoryOrLang
        }).catch(() => {});
        
        // Clear locally cached deck JSON from IndexedDB
        try {
          const key = `fc_store_deck_${native}_${target}_${deckId}`;
          const { localFlashcardDb } = await import('./flashcardLocalDb');
          await localFlashcardDb.deleteStoreDeck(key);
          console.log(`[FlashcardManager] Deleted store deck key from IndexedDB: ${key}`);
        } catch (e) {
          console.error("Failed to delete store deck key from IndexedDB:", e);
        }
      }

      // Clear from decks table & flashcards table
      await dbManager.from('decks', user.id).deleteMatch({ id: categoryOrLang }).catch(() => {});
      await dbManager.from('decks', user.id).deleteMatch({ name: categoryOrLang }).catch(() => {});
      await dbManager.from('flashcards', user.id).deleteMatch({ category: categoryOrLang }).catch(() => {});
      await dbManager.from('flashcards', user.id).deleteMatch({ lang_code: categoryOrLang }).catch(() => {});
      await dbManager.from('flashcards', user.id).deleteMatch({ deck_id: categoryOrLang }).catch(() => {});

      // Invalidate localStorage caches
      try {
        localStorage.removeItem(`libritui_cache_d1_flashcards_imported_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_decks_${user.id}`);
        localStorage.removeItem(`libritui_cache_all_cards_${user.id}`);
      } catch (e) {}

      window.dispatchEvent(new CustomEvent('flashcardsUpdated'));
    } catch (err) {
      console.error("[FlashcardSync] Failed to delete deck from D1:", err);
    }
  },

  /**
   * Rename an entire deck of flashcards in D1.
   */
  renameFlashcardDeck: async (oldName: string, newName: string): Promise<void> => {
    try {
      const user = await getCurrentUser();
      if (!user) return;

      // Update all cards with this category to the new name
      await dbManager.from('flashcards', user.id).update({ category: newName }, { category: oldName });
    } catch (error) {
      console.error('Error renaming flashcard deck:', error);
    }
  },

  /**
   * Persistent Delete Queue: Ensures D1 deletions happen at 1 req/sec max.
   */
  _isDeleting: false,

  enqueueDeletion: async (cardIds: string[]): Promise<void> => {
    const currentQueue = JSON.parse(localStorage.getItem('libritui_delete_queue') || '[]');
    const newQueue = [...new Set([...currentQueue, ...cardIds])];
    localStorage.setItem('libritui_delete_queue', JSON.stringify(newQueue));
    flashcardManager.processDeleteQueue();
  },

  
  getFilteredLogs: async (langCode?: string, deckId?: string, period: 'Dias' | 'Semanas' | 'Anos' = 'Dias'): Promise<any[]> => {
    try {
      const { data: { session } } = await (await import('./supabaseClient')).supabase.auth.getSession();
      const user = session?.user;
      if (!user) return [];

      const results = await dbManager.execute({
        action: 'get_flashcards_chart_stats',
        table: 'flashcards_review_logs',
        user_id: user.id,
        data: {
            lang_code: langCode,
            deck_id: deckId,
            period: period
        }
      });

      if (Array.isArray(results)) {
         return results.map(r => ({
             activity_date: r.date,
             cards_reviewed: r.cards_reviewed
         }));
      }
      return [];
    } catch (e) {
      console.error('Error fetching filtered logs', e);
      return [];
    }
  },

  processDeleteQueue: async (): Promise<void> => {
    if (flashcardManager._isDeleting) return;
    flashcardManager._isDeleting = true;

    try {
      while (true) {
        const queue = JSON.parse(localStorage.getItem('libritui_delete_queue') || '[]');
        if (queue.length === 0) break;

        const idToDelete = queue[0];
        try {
          await flashcardManager.deleteFlashcard(idToDelete);
          console.log(`[Delete Queue] Deleted card ${idToDelete}`);
        } catch (e) {
          console.error(`[Delete Queue] Failed to delete ${idToDelete}:`, e);
        }

        // Remove from queue
        const updatedQueue = JSON.parse(localStorage.getItem('libritui_delete_queue') || '[]');
        const nextQueue = updatedQueue.filter((id: string) => id !== idToDelete);
        localStorage.setItem('libritui_delete_queue', JSON.stringify(nextQueue));

        // Wait 1 second to rate limit
        await new Promise(r => setTimeout(r, 1000));
      }
    } finally {
      flashcardManager._isDeleting = false;
    }
  },

  /**
   * Garante que o baralho de palavras de um nível específico esteja importado e pronto no IndexedDB e D1.
   * Retorna { newlyImported: boolean, deckId: string, totalCards: number }.
   */
  ensureWordDeckImported: async (params: {
    userId: string;
    targetLang: string;
    level: string;
    nativeLang?: string;
  }): Promise<{ newlyImported: boolean; deckId: string; totalCards: number }> => {
    const { userId, targetLang, level, nativeLang = 'pt' } = params;
    const cleanTarget = (targetLang || 'en').toLowerCase().split('-')[0];
    const cleanNative = (nativeLang || 'pt').toLowerCase().split('-')[0];
    const lvlUpper = (level || 'A1').toUpperCase();
    const deckId = `words_${lvlUpper}`;
    const localKey = `fc_store_deck_${cleanNative}_${cleanTarget}_${deckId}`;

    // 1. Verificar se já existe no IndexedDB
    const existing = await localFlashcardDb.getStoreDeck(localKey);
    if (existing && Array.isArray(existing) && existing.length > 0) {
      return { newlyImported: false, deckId, totalCards: existing.length };
    }

    // 2. Fazer download de new-flashcards no R2
    const WORD_LANG_FOLDERS: Record<string, string> = {
      'en': 'Ingles', 'english': 'Ingles',
      'pt': 'Portugues', 'portuguese': 'Portugues',
      'fr': 'Frances', 'french': 'Frances',
      'es': 'Espanhol', 'spanish': 'Espanhol',
      'de': 'Alemao', 'german': 'Alemao',
      'it': 'Italiano', 'italian': 'Italiano',
      'ru': 'Russo', 'russian': 'Russo',
      'hu': 'Hungaro', 'hungarian': 'Hungaro'
    };
    const WORD_SHORT_CODES: Record<string, string> = {
      'en': 'en', 'english': 'en',
      'pt': 'pt', 'portuguese': 'pt',
      'fr': 'fr', 'french': 'fr',
      'es': 'es', 'spanish': 'es',
      'de': 'de', 'german': 'de',
      'it': 'it', 'italian': 'it',
      'ru': 'ru', 'russian': 'ru',
      'hu': 'hu', 'hungarian': 'hu'
    };

    const targetFolder = WORD_LANG_FOLDERS[cleanTarget] || 'Ingles';
    const targetCode = WORD_SHORT_CODES[cleanTarget] || 'en';
    const nativeCode = WORD_SHORT_CODES[cleanNative] || 'pt';

    const candidates = [
      `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/new-flashcards/${targetFolder}/${lvlUpper}/${targetCode}-${nativeCode}.json?t=${Date.now()}`,
      `https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/biblioteca-backup/new-flashcards/${targetFolder}/${lvlUpper}/${targetCode}-${nativeCode}.json?t=${Date.now()}`
    ];

    let rawList: any[] = [];
    for (const url of candidates) {
      try {
        const res = await fetch(url);
        if (res.ok) {
          const json = await res.json();
          if (Array.isArray(json)) { rawList = json; break; }
          if (json && json[lvlUpper] && Array.isArray(json[lvlUpper])) { rawList = json[lvlUpper]; break; }
        }
      } catch (e) {}
    }

    if (!rawList || rawList.length === 0) {
      return { newlyImported: false, deckId, totalCards: 0 };
    }

    const merged = rawList.map((item: any, idx: number) => {
      const primaryTrans = (Array.isArray(item.translations) && item.translations.length > 0)
        ? item.translations[0]
        : (item.translation || item.word || '');
      const secondaryTrans = (Array.isArray(item.translations) && item.translations.length > 1)
        ? item.translations.slice(1)
        : [];
      return {
        id_base: `w_${lvlUpper}_${item.id || idx + 1}`,
        word: item.word || '',
        translations: item.translations || [primaryTrans, ...secondaryTrans],
        primary_translation: primaryTrans,
        secondary_translations: secondaryTrans,
        [cleanTarget]: item.word || '',
        [cleanNative]: primaryTrans
      };
    });

    // Salvar no IndexedDB
    await localFlashcardDb.saveStoreDeck(localKey, merged);

    // Salvar no D1 Cloudflare
    if (userId && userId !== 'guest') {
      try {
        await dbManager.from('flashcards_imported', userId).insert({
          user_id: userId,
          deck_id: deckId,
          idioma_nativo: cleanNative,
          idioma_alvo: cleanTarget,
          caminho_local_do_arquivo: localKey
        });
      } catch (e) {}
    }

    try {
      localStorage.removeItem(`libritui_cache_d1_flashcards_imported_${userId}`);
      localStorage.removeItem(`libritui_cache_all_decks_${userId}`);
      localStorage.removeItem(`libritui_cache_all_cards_${userId}`);
      localStorage.removeItem(`libritui_cache_d1_flashcards_${userId}`);
    } catch (e) {}

    window.dispatchEvent(new CustomEvent('flashcardsUpdated'));
    return { newlyImported: true, deckId, totalCards: merged.length };
  },

  /**
   * Garante que o baralho da missão do plano (palavras ou frases) esteja salvo da loja e
   * devolve o id exato usado em /flashcards (`${deck_id}_${nativo}_${alvo}`).
   * O conteúdo é baixado pelo getFlashcards na primeira leitura, se ainda não estiver no IndexedDB.
   */
  ensurePlanDeckImported: async (params: {
    userId: string;
    targetLang: string;
    level: string;
    kind: 'words' | 'phrases';
    nativeLang?: string;
  }): Promise<string | null> => {
    const { userId, targetLang, level, kind } = params;
    if (!userId || userId === 'guest') return null;
    const cleanTarget = (targetLang || 'en').toLowerCase().split('-')[0];
    const lvlUpper = (level || 'A1').toUpperCase();
    const baseId = kind === 'words' ? `words_${lvlUpper}` : lvlUpper;

    // Idioma nativo no padrão da loja (Português = 'ptbr'); fora da loja cai para Português
    const STORE_NATIVES = ['ptbr', 'en', 'es', 'fr', 'de', 'it', 'hu', 'ru'];
    let native = (params.nativeLang || 'pt').toLowerCase().split('-')[0];
    if (native === 'pt') native = 'ptbr';
    if (!STORE_NATIVES.includes(native) || native === cleanTarget) native = 'ptbr';
    const nativeVariants = native === 'ptbr' ? ['ptbr', 'pt'] : [native];

    let importedList: any[] = [];
    const cacheImportedKey = `libritui_cache_d1_flashcards_imported_${userId}`;
    try {
      const res: any = await dbManager.from('flashcards_imported', userId).select({ user_id: userId });
      importedList = Array.isArray(res) ? res : (res?.data || []);
    } catch (e) {
      try { importedList = JSON.parse(localStorage.getItem(cacheImportedKey) || '[]'); } catch {}
    }

    const existing = importedList.find((d: any) =>
      String(d.deck_id).toUpperCase() === baseId.toUpperCase() &&
      (d.idioma_alvo || '').toLowerCase() === cleanTarget &&
      nativeVariants.includes((d.idioma_nativo || '').toLowerCase())
    );
    if (existing) return `${existing.deck_id}_${existing.idioma_nativo}_${existing.idioma_alvo}`;

    const localKey = `fc_store_deck_${native}_${cleanTarget}_${baseId}`;
    await dbManager.from('flashcards_imported', userId).insert({
      user_id: userId,
      deck_id: baseId,
      idioma_nativo: native,
      idioma_alvo: cleanTarget,
      caminho_local_do_arquivo: localKey
    });

    try {
      localStorage.removeItem(cacheImportedKey);
      localStorage.removeItem(`libritui_cache_all_decks_${userId}`);
      localStorage.removeItem(`libritui_cache_all_cards_${userId}`);
      localStorage.removeItem(`libritui_cache_d1_flashcards_${userId}`);
      localStorage.removeItem(`libritui_cache_store_imported_${userId}_${native}_${cleanTarget}`);
    } catch (e) {}

    window.dispatchEvent(new CustomEvent('flashcardsUpdated'));
    return `${baseId}_${native}_${cleanTarget}`;
  }
};

// Start the queue on boot
if (typeof window !== 'undefined') {
  setTimeout(() => flashcardManager.processDeleteQueue(), 2000);
}
